import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentFamily, refreshFamilies } = useFamily();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFamilyData = useCallback(async () => {
    if (!currentFamily) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(`/families/${currentFamily.id}/`);
      setFamily(response.data);
    } catch (error) {
      setError('Failed to load family information.');
    } finally {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    // Check for subscription status in URL params
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      setError(null);
      // Refresh family data to get updated subscription info
      if (currentFamily) {
        refreshFamilies();
        fetchFamilyData();
      }
    } else if (subscriptionStatus === 'canceled') {
      setError('Subscription checkout was canceled.');
    }
  }, [searchParams, currentFamily, fetchFamilyData, refreshFamilies]);

  useEffect(() => {
    fetchFamilyData();
  }, [fetchFamilyData]);

  const subscriptionPlans = [
    {
      tier: 'free',
      name: 'Free',
      price: '$0',
      period: 'Forever',
      features: [
        'Up to 10 family members',
        'Up to 5 events',
        'Up to 3 wishlists',
        'Basic event management',
        'Wishlist sharing',
        'Name picker',
        'Recipe sharing',
        'Basic customization',
      ],
      popular: false,
      disabled: false,
    },
    {
      tier: 'premium',
      name: 'Premium',
      price: '$5.99',
      originalPrice: '$9.99',
      period: 'per month',
      isPromotional: true,
      features: [
        'Up to 50 family members',
        'Unlimited events',
        'Unlimited wishlists',
        'Advanced customization',
        'Background images',
        'Priority support',
        'All free features',
      ],
      popular: true,
      disabled: false,
    },
    {
      tier: 'extended_family',
      name: 'Extended Family',
      price: '$29.99',
      period: 'per month',
      features: [
        'Unlimited family members',
        'Unlimited events',
        'Unlimited wishlists',
        'Advanced analytics',
        'Custom branding',
        'Dedicated support',
        'All premium features',
      ],
      popular: false,
      disabled: false,
    },
  ];

  const handleUpgrade = async (tier) => {
    if (!currentFamily || !family) {
      setError('Please select a family first.');
      return;
    }

    // Check if user is admin
    const userMember = family.members?.find((m) => m.user_id === user?.uid);
    const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);

    if (!isAdmin) {
      setError('Only family admins can manage subscriptions.');
      return;
    }

    // Don't allow upgrading to same tier
    if (family.subscription_tier === tier) {
      setError(`Your family is already on the ${subscriptionPlans.find(p => p.tier === tier)?.name} plan.`);
      return;
    }

    setUpgrading(true);
    setError(null);

    try {
      const successUrl = `${window.location.origin}/subscription?subscription=success`;
      const cancelUrl = `${window.location.origin}/subscription?subscription=canceled`;

      const response = await api.post(
        `/families/${family.id}/subscription/create-checkout/`,
        {
          tier: tier,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout session.');
        setUpgrading(false);
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to start checkout process. Please try again.'
      );
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading subscription information...
        </Typography>
      </Container>
    );
  }

  if (!currentFamily) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Please select or create a family first before managing subscriptions.
        </Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/families')}
        >
          Go to Families
        </Button>
      </Container>
    );
  }

  if (!family) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load family information.
        </Alert>
      </Container>
    );
  }

  const userMember = family.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
  const currentTier = family.subscription_tier || 'free';

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Only family admins can manage subscriptions. Please contact a family admin to upgrade your plan.
        </Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/families/${family.id}/dashboard`)}
        >
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Subscription Plans
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Family-Level Subscription • One subscription for the whole family
        </Typography>
        {family && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="primary">
              {family.name}
            </Typography>
            <Chip
              label={`Current Plan: ${subscriptionPlans.find(p => p.tier === currentTier)?.name || currentTier}`}
              color={currentTier === 'free' ? 'default' : 'primary'}
              sx={{ mt: 1 }}
            />
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity={error.includes('canceled') ? 'info' : 'error'} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {searchParams.get('subscription') === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Subscription updated successfully! Your family now has access to the new plan features.
        </Alert>
      )}

      <Grid container spacing={3} justifyContent="center">
        {subscriptionPlans.map((plan) => {
          const isCurrentPlan = currentTier === plan.tier;
          const isDowngrade = 
            (plan.tier === 'free' && currentTier !== 'free') ||
            (plan.tier === 'premium' && currentTier === 'extended_family');
          
          return (
            <Grid item xs={12} sm={6} md={4} key={plan.tier}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  border: plan.popular ? 2 : 1,
                  borderColor: plan.popular
                    ? 'primary.main'
                    : isCurrentPlan
                    ? 'success.main'
                    : 'divider',
                  boxShadow: plan.popular ? 4 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
                {isCurrentPlan && (
                  <Chip
                    label="Current Plan"
                    size="small"
                    color="success"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      fontWeight: 600,
                    }}
                  />
                )}
                <CardContent sx={{ pt: plan.popular || isCurrentPlan ? 6 : 3, flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                    {plan.name}
                  </Typography>
                  {plan.isPromotional && (
                    <Chip
                      label="Limited Time Offer"
                      color="error"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      {plan.originalPrice && (
                        <Typography
                          variant="h6"
                          component="span"
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.secondary',
                            fontWeight: 400,
                          }}
                        >
                          {plan.originalPrice}
                        </Typography>
                      )}
                      <Typography
                        variant="h4"
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: plan.isPromotional ? 'error.main' : 'inherit',
                        }}
                      >
                        {plan.price}
                      </Typography>
                      {plan.period !== 'Forever' && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          {' '}
                          {plan.period}
                        </Typography>
                      )}
                    </Box>
                    {plan.isPromotional && plan.originalPrice && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Save{' '}
                        {`$${(
                          parseFloat(plan.originalPrice.replace('$', '')) -
                          parseFloat(plan.price.replace('$', ''))
                        ).toFixed(2)}`}{' '}
                        per month!
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant={isCurrentPlan ? 'outlined' : 'contained'}
                    fullWidth
                    startIcon={
                      isCurrentPlan ? null : <UpgradeIcon />
                    }
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={
                      isCurrentPlan ||
                      isDowngrade ||
                      upgrading ||
                      plan.disabled
                    }
                    sx={{ mt: 2 }}
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : isDowngrade
                      ? 'Contact Support to Downgrade'
                      : upgrading
                      ? 'Processing...'
                      : `Upgrade to ${plan.name}`}
                  </Button>
                  {isDowngrade && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                      To downgrade, please cancel your current subscription from
                      the dashboard.
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help choosing a plan? Contact our support team.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/families/${family.id}/dashboard`)}
        >
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default Subscription;
